import { useEffect, useState } from 'react';
import { Check, X, AlertTriangle } from 'lucide-react';
import {
  calculatePasswordStrength,
  checkPasswordBreach,
  type PasswordStrengthResult,
  type BreachCheckResult,
} from '@/lib/auth/passwordStrength';

interface PasswordStrengthMeterProps {
  password: string;
  onBreachStatusChange?: (breached: boolean) => void;
}

const STRENGTH_CONFIG = {
  weak: { label: 'Weak', color: 'bg-destructive', textColor: 'text-destructive' },
  fair: { label: 'Fair', color: 'bg-warning', textColor: 'text-amber-600' },
  strong: { label: 'Strong', color: 'bg-primary', textColor: 'text-primary' },
  'very-strong': { label: 'Very Strong', color: 'bg-primary', textColor: 'text-primary' },
} as const;

export function PasswordStrengthMeter({ password, onBreachStatusChange }: PasswordStrengthMeterProps) {
  const [strength, setStrength] = useState<PasswordStrengthResult | null>(null);
  const [breachResult, setBreachResult] = useState<BreachCheckResult | null>(null);
  const [checkingBreach, setCheckingBreach] = useState(false);

  useEffect(() => {
    if (!password) {
      setStrength(null);
      setBreachResult(null);
      return;
    }
    setStrength(calculatePasswordStrength(password));
  }, [password]);

  useEffect(() => {
    if (!password || password.length < 8) {
      setBreachResult(null);
      onBreachStatusChange?.(false);
      return;
    }

    const timer = setTimeout(async () => {
      setCheckingBreach(true);
      const result = await checkPasswordBreach(password);
      setBreachResult(result);
      onBreachStatusChange?.(result.breached);
      setCheckingBreach(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [password]);

  if (!password || !strength) return null;

  const config = STRENGTH_CONFIG[strength.level];
  const widthPercent = ((strength.score + 1) / 5) * 100;

  return (
    <div className="space-y-3 rounded-xl border border-border bg-muted/50 p-4">
      {/* Strength bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-muted-foreground">Password Strength</span>
          <span className={`text-xs font-semibold ${config.textColor}`}>{config.label}</span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-border">
          <div
            className={`h-1.5 rounded-full transition-all duration-300 ${config.color}`}
            style={{ width: `${widthPercent}%` }}
          />
        </div>
      </div>

      {/* Requirements checklist */}
      <div className="space-y-1.5">
        <p className="text-xs font-semibold text-foreground">Requirements:</p>
        {strength.requirements.map((req) => (
          <div key={req.id} className="flex items-center gap-2">
            {req.met ? (
              <Check className="h-3.5 w-3.5 flex-shrink-0 text-primary" />
            ) : (
              <X className="h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
            )}
            <span
              className={`text-xs ${req.met ? 'text-primary' : 'text-muted-foreground'}`}
            >
              {req.text}
            </span>
          </div>
        ))}
      </div>

      {/* Breach warning */}
      {breachResult?.breached && (
        <div className="flex items-start gap-2 rounded-lg bg-destructive/10 p-2.5">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-destructive" />
          <p className="text-xs text-destructive">
            This password has appeared in {breachResult.count.toLocaleString()} known data breaches.
            Please choose a different password to continue.
          </p>
        </div>
      )}

      {checkingBreach && (
        <p className="text-xs text-muted-foreground">Checking password safety...</p>
      )}
    </div>
  );
}
